import Image, { ImageProps } from 'next/image';
import { useState, useCallback, useEffect, memo } from 'react';
import { getBunnyOptimizedSrc } from '@util/images/bunnyOptimizer';

interface SafeImageProps extends ImageProps {
  alt: string;
  height?: number;
  width?: number;
  src: string;
  fallbackSrc?: string;
  classes?: string;
  priority?: boolean;
  // Pass true for full-resolution zoom/lightbox views — bypasses Bunny transforms
  // so the original image is always served regardless of optimizer toggle state.
  disableOptimization?: boolean;
}

const ERROR_MAX_WIDTH = 96;

// getBunnyOptimizedSrc lives in @util/images/bunnyOptimizer so non-UI callers
// (productCardProps) can share it without importing next/image.

const SafeImage = ({
  src,
  height = 144,
  width = 144,
  alt,
  fallbackSrc = '/not_found.svg',
  fill = false,
  classes = '',
  priority = false,
  loading,
  disableOptimization = false,
  ...imageProps
}: SafeImageProps) => {
  // fill images: target 1600px (covers 800px containers at 2× DPR; Bunny won't upscale past source)
  // explicit-width images: 2× the display width, capped at 1600px
  const targetWidth = fill ? 1600 : Math.min((width || 288) * 2, 1600);
  const optimizedSrc = disableOptimization
    ? src
    : getBunnyOptimizedSrc(src, targetWidth);

  const [imgProps, setImgProps] = useState({
    width,
    height,
    src: optimizedSrc,
  });

  // Sync src prop changes (e.g. async-loaded data) and re-apply optimization.
  useEffect(() => {
    const tw = fill ? 1600 : Math.min((width || 288) * 2, 1600);
    const newSrc = disableOptimization ? src : getBunnyOptimizedSrc(src, tw);
    setImgProps(prev => prev.src === newSrc ? prev : { ...prev, src: newSrc });
  }, [src, disableOptimization, fill, width]);

  // Memoize the error handler to prevent recreation on rerenders
  const handleOnError = useCallback(() => {
    setImgProps({
      width: Math.min(ERROR_MAX_WIDTH, width),
      height: Math.min(ERROR_MAX_WIDTH, width),
      src: fallbackSrc || '/not_found.svg',
    });
  }, [fallbackSrc, width]);

  // Compute dimension props only once
  const dimensionProps = fill
    ? { fill: true }
    : { height: imgProps.height, width: imgProps.width };

  // Determine loading strategy
  const loadingStrategy = priority ? 'eager' : loading || 'lazy';

  // Use Next.js Image with optimal settings
  return (
    <Image
      alt={alt}
      src={imgProps.src || '/not_found.svg'}
      onError={handleOnError}
      {...dimensionProps}
      className={classes}
      priority={priority}
      {...imageProps}
      loading={loadingStrategy}
      itemProp="image"
      sizes={imageProps.sizes || "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"}
      quality={imageProps.quality || 75}
    />
  );
};

// Export a memoized version of the component to prevent unnecessary re-renders
export default memo(SafeImage);
